import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, eur } from "../lib/api";
import { Lines, Reveal, EASE } from "../components/site/Motion";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [reservation, setReservation] = useState(null);
  const [pending, setPending] = useState(true);
  const tries = useRef(0);

  useEffect(() => {
    document.title = "SWAPA prenotata — SWAPA";
    if (!sessionId) {
      setPending(false);
      return;
    }
    const poll = async () => {
      try {
        const s = await api.get(`/payments/status/${sessionId}`);
        if (s.data.payment_status === "paid") {
          const r = await api.get(`/preorders/by-session/${sessionId}`);
          setReservation(r.data);
          setPending(false);
          return;
        }
      } catch (e) { /* retry */ }
      tries.current += 1;
      if (tries.current < 15) setTimeout(poll, 2000);
      else setPending(false);
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen flex-col items-center justify-center bg-ink px-5 py-32 text-center">
      {pending ? (
        <div data-testid="payment-processing">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Verifica pagamento in corso…</p>
          <p className="mt-4 font-display text-3xl font-extrabold uppercase text-white">Un attimo.</p>
        </div>
      ) : reservation ? (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="w-full max-w-2xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Prenotazione confermata</p>
          <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
            <Lines animate="load" lines={["Your SWAPA", "is reserved."]} className="text-5xl text-white sm:text-7xl" />
          </h1>
          <Reveal delay={0.3}>
            <div data-testid="reservation-summary" className="mt-10 space-y-3 border border-white/10 bg-card p-6 text-left sm:p-8">
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Numero prenotazione</span><span data-testid="reservation-id" className="font-mono font-semibold text-swapa-yellow">{reservation.reservation_id}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Veicolo</span><span className="text-white">SWAPA™ — {reservation.colour_name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Prezzo veicolo</span><span className="text-white">{eur(reservation.vehicle_price)} IVA incl.</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Caparra versata</span><span className="text-white">{eur(reservation.deposit)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Intestatario</span><span className="text-white">{reservation.first_name} {reservation.last_name}</span></div>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-zinc-400">
              Riceverai a breve un'email di conferma con i prossimi passi. Nel frattempo,
              la città ti aspetta.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/test-drive" data-testid="success-testdrive-cta" className="bg-swapa-yellow px-8 py-4 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-black">
                Prenota il tuo test drive
              </Link>
              <Link to="/network" data-testid="success-network-cta" className="border border-white/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white hover:border-swapa-yellow">
                Trova il tuo centro SWAPA
              </Link>
            </div>
            <p className="mt-6 text-xs text-zinc-600">
              Puoi cancellare la prenotazione senza penali prima del contratto definitivo:{" "}
              <Link to="/recesso" data-testid="success-withdrawal-link" className="text-zinc-400 underline underline-offset-4 hover:text-swapa-yellow">
                Recedere dal contratto qui
              </Link>
            </p>
          </Reveal>
        </motion.div>
      ) : (
        <div data-testid="payment-unconfirmed">
          <h1 className="font-display text-3xl font-extrabold uppercase text-white">Pagamento in verifica</h1>
          <p className="mt-4 max-w-md text-sm text-zinc-400">
            Non abbiamo ancora ricevuto la conferma del pagamento. Se hai completato il pagamento,
            la conferma arriverà via email. Per assistenza scrivi a info@swapa.it.
          </p>
          <Link to="/" data-testid="success-home-link" className="mt-8 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-swapa-yellow">Torna alla home</Link>
        </div>
      )}
    </div>
  );
}
